import { isNil } from 'lodash-es'
import { Locale, Translation } from 'locale'
import {
  BasketAllocation,
  CommittingServicingEvent,
  Deal,
  DealDetail,
  DealEvent,
  DealStatus,
  FundAssignment,
  InitiatingActor,
  Market,
  MarketStatus,
  NegotiableFieldRequired,
  PlatformDetail,
  ServicingSideDetails,
  ServicingSideDetailsMeta,
  ServicingType,
  TradingStatus,
  UserPreference
} from 'api'
import { TypedRowNode } from 'view/components/datagrid'
import { getNotionalFormatOptions } from 'view/shared/datagrid'
import { getClearingTicketInfo, getInitiatorTradingEntityTicketInfo, getUserAndOrganisationTicketInfo } from 'view/shared/ticket'
import { compareInsensitive } from 'utils'
import * as constants from 'view/styles/constants'
import { iconSet } from 'view/components/icons'
import { ModificationType } from '../../requests/shared/deltaGetters'

export const getNegotiatedValue = <T>(field: NegotiableFieldRequired<T> | null | undefined) => {
  return field ? (field.modified ? field.modifiedValue : field.negotiated) : null
}

const canPerformEvent = (dealDetail: DealDetail, dealEvent: DealEvent) => {
  return dealDetail.availableEvents.includes(dealEvent)
}

export const canAdminCancel = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_START_ADMIN_CANCEL) || canPerformEvent(dealDetail, DealEvent.SERVICING_CANCEL)
}

export const canCancel = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.CANCEL)
}

export const canSaveAssignments = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SAVE_ASSIGNMENT)
}

export const canAssignTradingEntities = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.ASSIGN)
}

export const canSaveAllocations = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SAVE_ALLOCATIONS)
}

export const canExtendAllocationTime = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.EXTEND_ALLOCATION_TIME)
}

export const canAllocateCollaterals = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.ALLOCATE)
}

export const canClearingFailureManuallyCancel = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.CLEARING_FAILURE_MANUALLY_REJECTED)
}

export const canClearingFailureRetry = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.CLEARING_FAILURE_RETRY)
}

export const canClearingFailureManuallyComplete = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.CLEARING_FAILURE_MANUALLY_ACCEPTED)
}

export const canServicingStartAmend = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_START_AMEND)
}

export const canServicingTraderStartAmend = (dealDetail: DealDetail) => {
  return (
    canPerformEvent(dealDetail, DealEvent.SERVICING_START_TRADER_AMEND) ||
    (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_TRADER_AMEND &&
      dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
      canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingTraderCancel = (dealDetail: DealDetail) => {
  return (
    canPerformEvent(dealDetail, DealEvent.SERVICING_START_TRADER_CANCEL) ||
    (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_TRADER_CANCEL &&
      dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
      canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartSubstitute = (dealDetail: DealDetail) => {
  return (
    canPerformEvent(dealDetail, DealEvent.SERVICING_START_SUBSTITUTE) ||
    (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SUBSTITUTION &&
      [InitiatingActor.ORDER_INITIATOR, InitiatingActor.ORDER_RESPONDER].includes(dealDetail.deal.servicingDetails.servicingInitiator) &&
      dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
      canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingAdminStartSubstitute = (dealDetail: DealDetail) => {
  return (
    canPerformEvent(dealDetail, DealEvent.SERVICING_START_ADMIN_SUBSTITUTION) ||
    (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SUBSTITUTION &&
      dealDetail.deal.servicingDetails.servicingInitiator === InitiatingActor.ADMIN &&
      dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
      canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartRoll = (dealDetails: DealDetail[]) => {
  return dealDetails.every(dealDetail => canPerformEvent(dealDetail, DealEvent.SERVICING_START_ROLL))
}

export const canServicingStartRerate = (dealDetails: DealDetail[]) => {
  return dealDetails.every(
    dealDetail =>
      (canPerformEvent(dealDetail, DealEvent.SERVICING_START_RERATE) && dealDetail.deal.currency === dealDetails[0].deal.currency) ||
      (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.RERATE &&
        [InitiatingActor.ORDER_INITIATOR, InitiatingActor.ORDER_RESPONDER].includes(dealDetail.deal.servicingDetails.servicingInitiator) &&
        dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
        canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartAdminRerate = (dealDetails: DealDetail[]) => {
  return dealDetails.every(
    dealDetail =>
      (canPerformEvent(dealDetail, DealEvent.SERVICING_START_ADMIN_RERATE) && dealDetail.deal.currency === dealDetails[0].deal.currency) ||
      (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.RERATE &&
        dealDetail.deal.servicingDetails.servicingInitiator === InitiatingActor.ADMIN &&
        dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
        canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartCloseOpen = (dealDetails: DealDetail[]) => {
  return dealDetails.every(
    dealDetail =>
      canPerformEvent(dealDetail, DealEvent.SERVICING_START_RERATE) ||
      (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.CLOSE_OPEN &&
        dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
        [InitiatingActor.ORDER_INITIATOR, InitiatingActor.ORDER_RESPONDER].includes(dealDetail.deal.servicingDetails.servicingInitiator) &&
        canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartAdminCloseOpen = (dealDetails: DealDetail[]) => {
  return dealDetails.every(
    dealDetail =>
      canPerformEvent(dealDetail, DealEvent.SERVICING_START_ADMIN_RERATE) ||
      (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.CLOSE_OPEN &&
        dealDetail.deal.servicingDetails.servicingInitiator === InitiatingActor.ADMIN &&
        dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
        canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartReturnRecall = (dealDetail: DealDetail) => {
  return (
    canPerformEvent(dealDetail, DealEvent.SERVICING_START_RETURN_RECALL) ||
    (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.RETURN_RECALL &&
      dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
      canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartTerminate = (dealDetails: DealDetail[]) => {
  return dealDetails.every(
    dealDetail =>
      (canPerformEvent(dealDetail, DealEvent.SERVICING_START_TERMINATE) && dealDetail.deal.currency === dealDetails[0].deal.currency) ||
      (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.TERMINATION &&
        dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
        [InitiatingActor.ORDER_INITIATOR, InitiatingActor.ORDER_RESPONDER].includes(dealDetail.deal.servicingDetails.servicingInitiator) &&
        canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartAdminTerminate = (dealDetails: DealDetail[]) => {
  return dealDetails.every(
    dealDetail =>
      (canPerformEvent(dealDetail, DealEvent.SERVICING_START_ADMIN_TERMINATE) &&
        dealDetail.deal.currency === dealDetails[0].deal.currency) ||
      (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.TERMINATION &&
        dealDetail.deal.servicingDetails.servicingInitiator === InitiatingActor.ADMIN &&
        dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
        canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingStartRollInPlace = (dealDetails: DealDetail[]) => {
  return dealDetails.every(
    dealDetail =>
      canPerformEvent(dealDetail, DealEvent.SERVICING_START_ROLL_IN_PLACE) ||
      (dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.ROLL_IN_PLACE &&
        dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR &&
        canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES))
  )
}

export const canServicingSend = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_SEND)
}

export const canServicingUpdateAttributes = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_ATTRIBUTES)
}

export const canServicingRevertAttributes = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_REVERT_ATTRIBUTES)
}

export const canServicingUpdateRegulatoryAttributes = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_UPDATE_REGULATORY_ATTRIBUTES)
}

export const canServicingAccept = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_ACCEPT)
}

export const canServicingReject = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_REJECT)
}

export const canServicingWithdraw = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_WITHDRAW)
}

export const canServicingCancel = (dealDetail: DealDetail) => {
  return canPerformEvent(dealDetail, DealEvent.SERVICING_CANCEL)
}

export const canViewRelatedTrades = (dealDetails: DealDetail[]) =>
  dealDetails.length === 1 && isNil(dealDetails[0].deal.basketAllocation) && isNil(dealDetails[0].deal.fundAssignment)

export const collateralMarketNotSuspendedOrClosed = (dealDetails: DealDetail[], platformDetail: PlatformDetail | null) => {
  const euMarketDetails = platformDetail?.platform.marketDetails[Market.EUROPE]
  const usMarketDetails = platformDetail?.platform.marketDetails[Market.US]
  const euMarketOpenAndNotSuspended =
    euMarketDetails?.marketStatus === MarketStatus.MARKET_OPEN && euMarketDetails.tradingStatus === TradingStatus.ENABLED
  const usMarketOpenAndNotSuspended =
    (usMarketDetails?.marketStatus === MarketStatus.MARKET_OPEN || usMarketDetails?.marketStatus === MarketStatus.MARKET_PRE_OPEN) &&
    usMarketDetails.tradingStatus === TradingStatus.ENABLED

  return dealDetails.every(dealDetail => {
    const collateralMarket = dealDetail.product?.market
    const euCollateral = collateralMarket === Market.EUROPE
    const usCollateral = collateralMarket === Market.US
    return (euCollateral && euMarketOpenAndNotSuspended) || (usCollateral && usMarketOpenAndNotSuspended)
  })
}
export const allocationUnlocked = (basketAllocation: BasketAllocation | null, username: string) => {
  if (!basketAllocation) {
    return false
  }

  return basketAllocation.userIdWithLock === null || basketAllocation.userIdWithLock === username
}

export const assignmentUnlocked = (fundAssignment: FundAssignment | null, username: string) => {
  if (!fundAssignment) {
    return false
  }

  return fundAssignment.userIdWithLock === null || fundAssignment.userIdWithLock === username
}

export const isClearedDeal = (dealDetail: DealDetail) => {
  return dealDetail.deal.clearingHouseId !== null
}

export const isAmendedDeal = (dealDetail: DealDetail) => dealDetail.deal.amended === true

export const isRolledDeal = (dealDetail: DealDetail) => dealDetail.deal.rolled === true

export const isServicing = (dealDetail: DealDetail) =>
  dealDetail.deal.status === DealStatus.SERVICING_WITH_INITIATOR || dealDetail.deal.status === DealStatus.SERVICING_WITH_RESPONDER

export const getAdminUserDealStatusColor = (dealDetail: DealDetail) => {
  switch (dealDetail.deal.status) {
    case DealStatus.DONE:
    case DealStatus.COMPLETED:
      return constants.STATUS_COLOR_DONE

    case DealStatus.SETTLED:
    case DealStatus.ASSIGNED:
    case DealStatus.ALLOCATED:
    case DealStatus.CLEARING_NOT_SENT:
    case DealStatus.CLEARING_SENT:
    case DealStatus.CLEARING_ACKNOWLEDGED:
    case DealStatus.PREPARED:
      return constants.STATUS_COLOR_ACTIVE

    case DealStatus.CANCELLED:
    case DealStatus.UNASSIGNED:
    case DealStatus.UNALLOCATED:
    case DealStatus.REJECTED:
    case DealStatus.SERVICING_WITH_INITIATOR:
      return dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_ADMIN_CANCEL
        ? constants.STATUS_COLOR_ACTIVE
        : constants.STATUS_COLOR_DEFAULT
    case DealStatus.SERVICING_WITH_RESPONDER:
      return constants.STATUS_COLOR_DEFAULT

    case DealStatus.CLEARING_FAILED:
      return constants.ACCENT_BAD

    default:
      return constants.STATUS_COLOR_DEFAULT
  }
}

export const getTradingUserDealStatusColor = (dealDetail: DealDetail) => {
  switch (dealDetail.deal.status) {
    case DealStatus.DONE:
    case DealStatus.COMPLETED:
      return constants.STATUS_COLOR_DONE

    case DealStatus.SETTLED:
    case DealStatus.ASSIGNED:
    case DealStatus.ALLOCATED:
    case DealStatus.CLEARING_NOT_SENT:
    case DealStatus.CLEARING_SENT:
    case DealStatus.CLEARING_ACKNOWLEDGED:
    case DealStatus.PREPARED:
    case DealStatus.CLEARING_FAILED:
      return constants.STATUS_COLOR_ACTIVE

    case DealStatus.CANCELLED:
    case DealStatus.UNASSIGNED:
    case DealStatus.UNALLOCATED:
    case DealStatus.REJECTED:
    case DealStatus.SERVICING_WITH_RESPONDER:
      return constants.STATUS_COLOR_DEFAULT

    case DealStatus.SERVICING_WITH_INITIATOR:
      return dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_AMEND ||
        dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_TRADER_AMEND ||
        dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_TRADER_CANCEL ||
        dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_ADMIN_CANCEL
        ? constants.STATUS_COLOR_ACTIVE
        : constants.STATUS_COLOR_DEFAULT

    default:
      return constants.STATUS_COLOR_DEFAULT
  }
}

export const getDealStatusColor = (dealDetail: DealDetail, isAdmin: boolean) => {
  return isAdmin ? getAdminUserDealStatusColor(dealDetail) : getTradingUserDealStatusColor(dealDetail)
}

export const getAdminUserDealStatusText = (dealDetail: DealDetail) => {
  switch (dealDetail.deal.status) {
    case DealStatus.DONE:
    case DealStatus.COMPLETED:
    case DealStatus.SETTLED:
      return isAmendedDeal(dealDetail)
        ? Locale.translate(Translation.TC_AMENDED)
        : isRolledDeal(dealDetail)
        ? Locale.translate(Translation.TC_ROLLED)
        : isClearedDeal(dealDetail)
        ? Locale.translate(Translation.TC_ACCEPTED)
        : Locale.translate(Translation.TC_DONE)

    case DealStatus.CANCELLED:
      return Locale.translate(Translation.TC_CANCELLED)

    case DealStatus.UNASSIGNED:
      return Locale.translate(Translation.TC_UNASSIGNED)

    case DealStatus.ASSIGNED:
      return Locale.translate(Translation.TC_ASSIGNED)

    case DealStatus.UNALLOCATED:
      return Locale.translate(Translation.TC_UNALLOCATED)

    case DealStatus.ALLOCATED:
      return Locale.translate(Translation.TC_ALLOCATED)

    case DealStatus.CLEARING_NOT_SENT:
    case DealStatus.PREPARED:
      return Locale.translate(Translation.TC_CLEARING_REQUIRED)

    case DealStatus.CLEARING_SENT:
    case DealStatus.CLEARING_ACKNOWLEDGED:
      return Locale.translate(Translation.TC_CLEARING_PENDING)

    case DealStatus.CLEARING_FAILED:
      return Locale.translate(Translation.TC_CLEARING_FAILED)

    case DealStatus.REJECTED:
      return Locale.translate(Translation.TC_CLEARING_REJECTED)

    case DealStatus.EXPIRED_ALLOCATION:
      return Locale.translate(Translation.TC_EXPIRED_ALLOCATION)

    case DealStatus.SERVICING_WITH_INITIATOR:
    case DealStatus.SERVICING_WITH_RESPONDER:
    case DealStatus.SERVICING_WITH_WORKFLOW:
      switch (dealDetail.deal.servicingDetails?.servicingType.servicingType) {
        case ServicingType.SUBSTITUTION:
        case ServicingType.CLOSE_OPEN:
        case ServicingType.RERATE:
        case ServicingType.RETURN_RECALL:
        case ServicingType.TERMINATION:
        case ServicingType.ROLL_IN_PLACE:
        case ServicingType.ROLL:
          return Locale.translate(Translation.TC_PENDING)

        case ServicingType.SERVICING_AMEND:
        case ServicingType.SERVICING_TRADER_AMEND:
          return Locale.translate(Translation.TC_AMENDING)

        case ServicingType.SERVICING_TRADER_CANCEL:
          return Locale.translate(Translation.TC_CANCELLING)

        case ServicingType.SERVICING_ADMIN_CANCEL:
          return Locale.translate(Translation.TC_CANCELLING)

        default:
          return '-- --'
      }

    default:
      return '-- --'
  }
}

export const getTradingUserDealStatusText = (dealDetail: DealDetail) => {
  const clearedDeal = isClearedDeal(dealDetail)
  switch (dealDetail.deal.status) {
    case DealStatus.DONE:
    case DealStatus.COMPLETED:
    case DealStatus.SETTLED:
      return isAmendedDeal(dealDetail)
        ? Locale.translate(Translation.TC_AMENDED)
        : isRolledDeal(dealDetail)
        ? Locale.translate(Translation.TC_ROLLED)
        : isClearedDeal(dealDetail)
        ? Locale.translate(Translation.TC_ACCEPTED)
        : Locale.translate(Translation.TC_DONE)

    case DealStatus.CANCELLED:
      return Locale.translate(Translation.TC_CANCELLED)

    case DealStatus.UNASSIGNED:
      return Locale.translate(Translation.TC_UNASSIGNED)

    case DealStatus.ASSIGNED:
      return Locale.translate(Translation.TC_ASSIGNED)

    case DealStatus.UNALLOCATED:
      return Locale.translate(Translation.TC_UNALLOCATED)

    case DealStatus.ALLOCATED:
      return Locale.translate(Translation.TC_ALLOCATED)

    case DealStatus.CLEARING_NOT_SENT:
    case DealStatus.CLEARING_SENT:
    case DealStatus.CLEARING_ACKNOWLEDGED:
    case DealStatus.CLEARING_FAILED:
    case DealStatus.PREPARED:
      return Locale.translate(Translation.TC_PENDING)

    case DealStatus.REJECTED:
      return Locale.translate(Translation.TC_REJECTED)

    case DealStatus.EXPIRED_ALLOCATION:
      return Locale.translate(Translation.TC_EXPIRED_ALLOCATION)

    case DealStatus.SERVICING_WITH_RESPONDER:
    case DealStatus.SERVICING_WITH_INITIATOR:
    case DealStatus.SERVICING_WITH_WORKFLOW:
      switch (dealDetail.deal.servicingDetails?.servicingType.servicingType) {
        case ServicingType.SUBSTITUTION:
        case ServicingType.CLOSE_OPEN:
        case ServicingType.RERATE:
        case ServicingType.TERMINATION:
        case ServicingType.RETURN_RECALL:
        case ServicingType.ROLL_IN_PLACE:
        case ServicingType.ROLL:
          return Locale.translate(Translation.TC_PENDING)

        case ServicingType.SERVICING_AMEND:
          return Locale.translate(
            dealDetail.deal.amended ? Translation.TC_AMENDED : clearedDeal ? Translation.TC_ACCEPTED : Translation.TC_DONE
          )

        case ServicingType.SERVICING_TRADER_AMEND:
          return Locale.translate(Translation.TC_AMENDING)

        case ServicingType.SERVICING_TRADER_CANCEL:
          return Locale.translate(Translation.TC_CANCELLING)

        case ServicingType.SERVICING_ADMIN_CANCEL:
          return Locale.translate(Translation.TC_CANCELLING)
        default:
          return '-- --'
      }

    default:
      return '-- --'
  }
}

export const getDealStatusText = (dealDetail: DealDetail, isAdmin: boolean) => {
  return isAdmin ? getAdminUserDealStatusText(dealDetail) : getTradingUserDealStatusText(dealDetail)
}

export const getDealStatusTextDone = (dealDetail: DealDetail, isAdmin: boolean) => {
  switch (dealDetail.deal.status) {
    case DealStatus.DONE:
    case DealStatus.COMPLETED:
    case DealStatus.SETTLED:
      return isClearedDeal(dealDetail) ? Locale.translate(Translation.TC_ACCEPTED) : Locale.translate(Translation.TC_DONE)

    case DealStatus.SERVICING_WITH_INITIATOR:
      switch (dealDetail.deal.servicingDetails?.servicingType.servicingType) {
        case ServicingType.SUBSTITUTION:
        case ServicingType.CLOSE_OPEN:
        case ServicingType.RERATE:
        case ServicingType.RETURN_RECALL:
        case ServicingType.TERMINATION:
        case ServicingType.ROLL_IN_PLACE:
          return Locale.translate(Translation.TC_PENDING)

        case ServicingType.SERVICING_AMEND:
          return isAdmin
            ? Locale.translate(Translation.TC_AMENDING)
            : isClearedDeal(dealDetail)
            ? Locale.translate(Translation.TC_ACCEPTED)
            : Locale.translate(Translation.TC_DONE)

        default:
          return '-- --'
      }

    default:
      return getDealStatusText(dealDetail, isAdmin)
  }
}

export const getDealTitle = (dealDetail: DealDetail) => {
  const { ownOrder, orderFundFirm, counterpartyFirm, clearingHouse, sponsor, counterpartySponsor } = dealDetail
  const userAndOrganisationInfo = getUserAndOrganisationTicketInfo(
    ownOrder,
    orderFundFirm,
    counterpartyFirm,
    dealDetail.deal.traderUserInitials,
    dealDetail.deal.counterpartyTraderInitials
  )
  const clearingInfo = getClearingTicketInfo(clearingHouse, ownOrder, sponsor, counterpartySponsor)
  return `${userAndOrganisationInfo}${clearingInfo}`
}

export const getDealKeyInfo = (dealDetail: DealDetail) => {
  const { ownOrder, orderFundFirm, orderFund, counterpartyFirm, clearingHouse, sponsor, counterpartySponsor } = dealDetail
  const userAndOrganisationInfo = getUserAndOrganisationTicketInfo(
    ownOrder,
    orderFundFirm,
    counterpartyFirm,
    dealDetail.deal.traderUserInitials,
    dealDetail.deal.counterpartyTraderInitials
  )
  const initiatorTradingEntity = getInitiatorTradingEntityTicketInfo(ownOrder, orderFund)
  const clearingInfo = getClearingTicketInfo(clearingHouse, ownOrder, sponsor, counterpartySponsor)

  return `${userAndOrganisationInfo}${initiatorTradingEntity}${clearingInfo}`
}

export const getDealHeadline = (dealDetail: DealDetail, userPreference: UserPreference) => {
  const { deal, collateral, collateralBasket } = dealDetail

  const instrumentName = isNil(collateral) ? (isNil(collateralBasket) ? '' : `${collateralBasket.name} `) : `${collateral.name} `
  const notionalString = isNil(deal.notional)
    ? ''
    : `${Locale.formatNumber(deal.notional, getNotionalFormatOptions(userPreference.amountFormat, true))} `
  const dateString =
    deal.startDate && deal.endDate
      ? `${Locale.formatDate(deal.startDate, userPreference.dateFormat)} - ${Locale.formatDate(deal.endDate, userPreference.dateFormat)}`
      : ''

  return `${instrumentName}${notionalString}${dateString}`
}

export const getDealEventText = (dealEvent: DealEvent) => {
  switch (dealEvent) {
    case DealEvent.CLEARING_FAILURE_MANUALLY_ACCEPTED:
      return Locale.translate(Translation.TC_CLEARING_MANUALLY_ACCEPTED)
    case DealEvent.CLEARING_FAILURE_MANUALLY_REJECTED:
      return Locale.translate(Translation.TC_CLEARING_MANUALLY_REJECTED)
    default:
      return dealEvent.toString()
  }
}

export function isImportantServicingEvent(servicingEvent: CommittingServicingEvent) {
  return [DealEvent.SERVICING_ACCEPT, DealEvent.SERVICING_REJECT, DealEvent.SERVICING_WITHDRAW].includes(servicingEvent)
}

export function getServicingStatusColor(servicingEvent: CommittingServicingEvent) {
  switch (servicingEvent) {
    case DealEvent.SERVICING_START_RERATE:
    case DealEvent.SERVICING_START_SUBSTITUTE:
    case DealEvent.SERVICING_START_TERMINATE:
    case DealEvent.SERVICING_START_RETURN_RECALL:
    case DealEvent.SERVICING_START_ROLL_IN_PLACE:
    case DealEvent.SERVICING_START_TRADER_AMEND:
    case DealEvent.SERVICING_SEND:
      return constants.STATUS_COLOR_RESPOND

    case DealEvent.SERVICING_ACCEPT:
      return constants.STATUS_COLOR_ACCEPTED

    case DealEvent.SERVICING_REJECT:
    case DealEvent.SERVICING_WITHDRAW:
      return constants.STATUS_COLOR_URGENT

    default:
      return 'initial'
  }
}

const getServicingTypeStr = (servicingType?: ServicingType) => {
  switch (servicingType) {
    case ServicingType.SUBSTITUTION:
      return Locale.translate(Translation.TC_SUBSTITUTION)
    case ServicingType.CLOSE_OPEN:
      return Locale.translate(Translation.TC_CLOSE_OPEN)
    case ServicingType.RERATE:
      return Locale.translate(Translation.TC_RERATE)
    case ServicingType.RETURN_RECALL:
      return Locale.translate(Translation.TC_RETURN_RECALL)
    case ServicingType.TERMINATION:
      return Locale.translate(Translation.TC_TERMINATION)
    case ServicingType.ROLL_IN_PLACE:
      return Locale.translate(Translation.TC_ROLL_IN_PLACE)
    case ServicingType.SERVICING_TRADER_AMEND:
      return Locale.translate(Translation.TC_AMEND_TRADE)
    case ServicingType.SERVICING_TRADER_CANCEL:
      return Locale.translate(Translation.TC_CANCEL_TRADE)
    case ServicingType.SERVICING_ADMIN_CANCEL:
      return Locale.translate(Translation.TC_CANCEL_TRADE)
    default:
      return ''
  }
}

export function getServicingStatusText(servicingEvent: CommittingServicingEvent, servicingType?: ServicingType) {
  switch (servicingEvent) {
    case DealEvent.SERVICING_ACCEPT:
      return `${getServicingTypeStr(servicingType)} ${Locale.translate(Translation.TC_ACCEPTED)}`.trim()

    case DealEvent.SERVICING_REJECT:
      return `${getServicingTypeStr(servicingType)} ${Locale.translate(Translation.TC_REJECTED)}`.trim()

    case DealEvent.SERVICING_WITHDRAW:
      return `${getServicingTypeStr(servicingType)} ${Locale.translate(Translation.TC_WITHDRAWN)}`.trim()

    default:
      return getServicingTypeStr(servicingType)
  }
}

export function getServicingHeadline(
  servicingSideDetails: ServicingSideDetails,
  servicingSideDetailsMeta: ServicingSideDetailsMeta | null,
  userPreference: UserPreference
) {
  const instrument = servicingSideDetailsMeta?.COLLATERAL
  const notional = getNegotiatedValue(servicingSideDetails?.negotiationFields.NOTIONAL?.negotiableFieldRequired)
  const startDate = getNegotiatedValue(servicingSideDetails?.negotiationFields.START_DATE?.negotiableFieldRequired)
  const endDate = getNegotiatedValue(servicingSideDetails?.negotiationFields.END_DATE?.negotiableFieldRequired)

  const instrumentName = isNil(instrument) ? '' : `${instrument.name} `
  const notionalString = isNil(notional)
    ? ''
    : `${Locale.formatNumber(notional, getNotionalFormatOptions(userPreference.amountFormat, true))} `
  const dateString =
    startDate && endDate
      ? `${Locale.formatDate(startDate, userPreference.dateFormat)} - ${Locale.formatDate(endDate, userPreference.dateFormat)}`
      : ''

  return `${instrumentName}${notionalString}${dateString}`
}

export const basketDealIdComparator = (
  basketDealId1: string,
  basketDealId2: string,
  rowItem1: TypedRowNode<DealDetail>,
  rowItem2: TypedRowNode<DealDetail>
) => {
  const dealDetail1 = rowItem1?.data
  const dealDetail2 = rowItem2?.data

  if (!dealDetail1 || !dealDetail2) {
    return 0
  }

  if (basketDealId1 === basketDealId2 && !isNil(basketDealId1)) {
    return !!dealDetail1.deal.basketAllocation ? -1 : !!dealDetail2.deal.basketAllocation ? 1 : 0
  } else {
    return compareInsensitive(basketDealId1, basketDealId2)
  }
}

export const getDealSubstatusText = (deal: Deal) =>
  deal.amended && deal.status !== DealStatus.CANCELLED ? Locale.translate(Translation.TC_AMENDED) : ''

export const getServicingIcon = (servicingEvent: CommittingServicingEvent) => {
  switch (servicingEvent) {
    case DealEvent.SERVICING_ACCEPT:
      return iconSet.tickIcon

    case DealEvent.SERVICING_REJECT:
    case DealEvent.SERVICING_WITHDRAW:
      return iconSet.thumbsDownIcon

    default:
      return null
  }
}

export const getDealIcon = (status: DealStatus) => {
  switch (status) {
    case DealStatus.DONE:
    case DealStatus.COMPLETED:
    case DealStatus.SETTLED:
    case DealStatus.CLEARING_NOT_SENT:
    case DealStatus.CLEARING_SENT:
    case DealStatus.CLEARING_ACKNOWLEDGED:
    case DealStatus.PREPARED:
    case DealStatus.SERVICING_WITH_INITIATOR:
    case DealStatus.SERVICING_WITH_RESPONDER:
      return iconSet.tickIcon

    case DealStatus.CANCELLED:
    case DealStatus.REJECTED:
    case DealStatus.CLEARING_FAILED:
      return iconSet.noEntryIcon

    default:
      return null
  }
}

export const getDealModificationColour = (
  getModificationType: (dealDetail?: DealDetail) => ModificationType | null,
  dealDetail: DealDetail | null
) => {
  if (!dealDetail) {
    return null
  }

  const modificationType = getModificationType(dealDetail)

  switch (modificationType) {
    case ModificationType.INTERNAL:
      return constants.ACCENT_PRIMARY
    case ModificationType.INITIATOR:
      return dealDetail.ownOrder ? constants.ACCENT_PRIMARY_3 : constants.ACCENT_SECONDARY_2
    case ModificationType.RESPONDER:
      return dealDetail.ownOrder ? constants.ACCENT_SECONDARY_2 : constants.ACCENT_PRIMARY_3
    default:
      return null
  }
}
